import { useTranslation } from 'react-i18next';
import { Bot, CheckCheck, CheckCircle2, MessagesSquare, Send, UsersRound } from 'lucide-react';

import { cn } from '@/lib/utils';

interface DotGridProps {
    readonly className?: string;
}

function DotGrid({ className }: DotGridProps) {
    return (
        <div aria-hidden="true" className={cn('grid w-fit grid-cols-6 gap-2.5', className)}>
            {Array.from({ length: 24 }).map((_, i) => (
                <span key={i} className="size-1 rounded-full bg-primary-200" />
            ))}
        </div>
    );
}

interface FloatingCardProps {
    readonly className?: string;
    readonly children: React.ReactNode;
}

function FloatingCard({ className, children }: FloatingCardProps) {
    return (
        <div
            className={cn(
                'absolute z-10 rounded-xl border border-border-default bg-bg-surface p-3 shadow-lg shadow-neutral-900/5 dark:shadow-none',
                className,
            )}
        >
            {children}
        </div>
    );
}

interface IconChipProps {
    readonly className?: string;
    readonly children: React.ReactNode;
}

function IconChip({ className, children }: IconChipProps) {
    return (
        <span
            className={cn(
                'flex size-7 shrink-0 items-center justify-center rounded-md [&>svg]:size-3.5',
                className,
            )}
        >
            {children}
        </span>
    );
}

interface RateBarProps {
    readonly barClassName: string;
}

function RateBar({ barClassName }: RateBarProps) {
    return (
        <div className="h-1 w-full overflow-hidden rounded-full bg-bg-inset">
            <div className={cn('h-full rounded-full', barClassName)} />
        </div>
    );
}

function PhoneMockup() {
    const { t } = useTranslation();

    return (
        <div className="absolute start-1/2 top-0 z-[5] w-60 -translate-x-1/2 rounded-[1.75rem] border border-border-default bg-bg-surface p-4 shadow-xl shadow-neutral-900/5 rtl:translate-x-1/2 dark:shadow-none">
            <div className="mx-auto mb-4 h-1.5 w-14 rounded-full bg-neutral-200 dark:bg-neutral-700" />
            <div className="max-w-[90%] rounded-2xl rounded-ss-sm bg-primary-500 px-3.5 py-2.5 text-xs leading-relaxed text-white">
                {t('auth.marketing.chatOutbound')}
            </div>
            <div className="mt-1.5 flex items-center justify-end gap-1 text-[10px] text-text-tertiary">
                {t('auth.marketing.chatDelivered')}
                <CheckCheck className="size-3 text-success-500" />
            </div>
            <div className="mt-3 w-fit max-w-[80%] rounded-2xl rounded-ss-sm bg-bg-raised px-3.5 py-2.5 text-xs leading-relaxed text-text-primary">
                {t('auth.marketing.chatReply')}
            </div>
            <div className="mt-3 flex w-fit items-center gap-1 rounded-2xl bg-bg-raised px-3.5 py-3">
                {Array.from({ length: 3 }).map((_, i) => (
                    <span key={i} className="size-1.5 rounded-full bg-neutral-400" />
                ))}
            </div>
            <div className="mt-5 flex items-center gap-2 rounded-full border border-border-default bg-bg-page py-1.5 ps-3.5 pe-1.5">
                <span className="flex-1 truncate text-xs text-text-tertiary">
                    {t('auth.marketing.chatPlaceholder')}
                </span>
                <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary-500 text-white">
                    <Send className="size-3" />
                </span>
            </div>
        </div>
    );
}

function CampaignStatsCard() {
    const { t } = useTranslation();

    return (
        <FloatingCard className="start-0 top-8 w-40">
            <div className="flex items-center gap-2">
                <IconChip className="bg-primary-50 text-primary-600">
                    <Send />
                </IconChip>
                <p className="text-xs font-semibold text-text-heading">
                    {t('auth.marketing.smsCampaigns')}
                </p>
            </div>
            <div className="mt-3 flex flex-col gap-2">
                <div className="flex items-center justify-between text-xs">
                    <span className="text-text-secondary">{t('auth.marketing.statSent')}</span>
                    <span className="font-semibold text-text-primary">12,480</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                    <span className="text-text-secondary">{t('auth.marketing.statDelivered')}</span>
                    <span className="font-semibold text-text-primary">98%</span>
                </div>
                <RateBar barClassName="w-[98%] bg-success-400" />
                <div className="flex items-center justify-between text-xs">
                    <span className="text-text-secondary">{t('auth.marketing.statReplied')}</span>
                    <span className="font-semibold text-text-primary">24%</span>
                </div>
                <RateBar barClassName="w-[24%] bg-primary-400" />
            </div>
        </FloatingCard>
    );
}

function AuthMarketingPanel() {
    const { t } = useTranslation();
    const brand = t('common.brandName');

    return (
        <div className="relative flex h-full w-full flex-col items-center justify-center overflow-hidden px-10 py-16">
            {/* Ambient brand glow behind the collage */}
            <div
                aria-hidden="true"
                className="absolute start-1/2 top-1/2 size-[34rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary-100/60 blur-3xl rtl:translate-x-1/2 dark:bg-primary-50/40"
            />

            <DotGrid className="absolute start-10 top-14" />
            <DotGrid className="absolute end-14 top-24" />
            <DotGrid className="absolute bottom-8 start-10" />

            <div className="origin-center scale-[0.82] min-[1200px]:scale-100">
                <div className="relative h-[470px] w-[520px]">
                    {/* Orbit rings from /public; masked so they still recolor in dark mode */}
                    <div
                        aria-hidden="true"
                        className="absolute inset-0 h-full w-full bg-primary-200 mask-[url(/auth-orbit-rings.svg)] mask-no-repeat mask-size-[100%_100%]"
                    />

                    <PhoneMockup />

                    <CampaignStatsCard />

                    <FloatingCard className="start-2 top-60">
                        <div className="flex items-center gap-2.5">
                            <IconChip className="bg-primary-50 text-primary-600">
                                <UsersRound />
                            </IconChip>
                            <div>
                                <p className="text-[10px] text-text-secondary">
                                    {t('auth.marketing.contacts')}
                                </p>
                                <p className="text-sm font-bold text-text-heading">2,340</p>
                            </div>
                        </div>
                    </FloatingCard>

                    <FloatingCard className="end-0 top-10">
                        <div className="flex items-center gap-2.5">
                            <IconChip className="bg-success-50 text-success-600 dark:bg-success-900 dark:text-success-300">
                                <CheckCircle2 />
                            </IconChip>
                            <div>
                                <p className="text-[10px] text-text-secondary">
                                    {t('auth.marketing.deliveryRate')}
                                </p>
                                <p className="text-sm font-bold text-text-heading">98%</p>
                            </div>
                        </div>
                    </FloatingCard>

                    <FloatingCard className="end-0 top-44">
                        <div className="flex items-center gap-2.5">
                            <IconChip className="bg-primary-50 text-primary-600">
                                <Bot />
                            </IconChip>
                            <div>
                                <p className="text-xs font-semibold text-text-heading">
                                    {t('auth.marketing.aiAgents')}
                                </p>
                                <p className="flex items-center gap-1 text-[10px] text-text-secondary">
                                    <span className="size-1.5 rounded-full bg-success-500" />
                                    {t('auth.marketing.aiAgentsStatus')}
                                </p>
                            </div>
                        </div>
                    </FloatingCard>

                    <FloatingCard className="bottom-8 start-14">
                        <div className="flex items-center gap-2.5">
                            <IconChip className="bg-primary-50 text-primary-600">
                                <MessagesSquare />
                            </IconChip>
                            <div>
                                <p className="text-xs font-semibold text-text-heading">
                                    {t('auth.marketing.twoWayTitle')}
                                </p>
                                <p className="text-[10px] text-text-secondary">
                                    {t('auth.marketing.twoWaySubtitle')}
                                </p>
                            </div>
                        </div>
                    </FloatingCard>
                </div>
            </div>

            <div className="mt-10 max-w-md text-center">
                <h2 className="text-3xl font-bold text-balance text-text-heading">
                    {t('auth.marketing.headline')}
                </h2>
                <p className="mt-4 text-sm leading-relaxed text-text-secondary">
                    {t('auth.marketing.subheadline', { brand })}
                </p>
            </div>
        </div>
    );
}

export { AuthMarketingPanel };
